import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import Layout from "./components/Layout";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import FamigliePage from "./pages/FamigliePage";
import FamigliaDetailPage from "./pages/FamigliaDetailPage";
import MagazzinoPage from "./pages/MagazzinoPage";
import DonatoriPage from "./pages/DonatoriPage";
import DonazioniPage from "./pages/DonazioniPage";
import { DistribuzioniPage, NuovaDistribuzionePage, ModificaDistribuzionePage } from "./pages/DistribuzioniPage";
import ReportPage from "./pages/ReportPage";
import UtentiPage from "./pages/UtentiPage";
import ImpostazioniPage from "./pages/ImpostazioniPage";
import ChiesePage from "./pages/ChiesePage";
import ImportPage from "./pages/ImportPage";
import PortaleFamiglia from "./pages/PortaleFamiglia";
import NewsPage from "./pages/NewsPage";
import LandingPage from "./pages/LandingPage";
import ContattiPage from "./pages/ContattiPage";
import AIConfigPage from "./pages/AIConfigPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// App Routes
const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const defaultRoute = user?.ruolo === 'famiglia' ? '/portale' : '/dashboard';

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={user ? <Navigate to={defaultRoute} replace /> : <LoginPage />} 
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/famiglie"
        element={
          <ProtectedRoute>
            <FamigliePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/famiglie/:id"
        element={
          <ProtectedRoute>
            <FamigliaDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/magazzino"
        element={
          <ProtectedRoute>
            <MagazzinoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/donatori"
        element={
          <ProtectedRoute>
            <DonatoriPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/donazioni"
        element={
          <ProtectedRoute>
            <DonazioniPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/distribuzioni"
        element={
          <ProtectedRoute>
            <DistribuzioniPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/distribuzioni/nuova"
        element={
          <ProtectedRoute>
            <NuovaDistribuzionePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/distribuzioni/modifica/:id"
        element={
          <ProtectedRoute>
            <ModificaDistribuzionePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/report"
        element={
          <ProtectedRoute>
            <ReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/utenti"
        element={
          <ProtectedRoute>
            <UtentiPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/impostazioni"
        element={
          <ProtectedRoute>
            <ImpostazioniPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chiese"
        element={
          <ProtectedRoute>
            <ChiesePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/import"
        element={
          <ProtectedRoute>
            <ImportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portale"
        element={
          <ProtectedRoute>
            <PortaleFamiglia />
          </ProtectedRoute>
        }
      />
      <Route
        path="/news"
        element={
          <ProtectedRoute>
            <NewsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contatti"
        element={
          <ProtectedRoute>
            <ContattiPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/config-ai"
        element={
          <ProtectedRoute>
            <AIConfigPage />
          </ProtectedRoute>
        }
      />

      {/* Default Redirect */}
      <Route path="/" element={user ? <Navigate to={defaultRoute} replace /> : <LandingPage />} />
      <Route path="*" element={user ? <Navigate to={defaultRoute} replace /> : <LandingPage />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
